import { Component, effect, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatTableDataSource } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { TableStateService } from '@core/services/table-state.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { EPlatform } from '@enums/campaign.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IChannel, ICreator } from '@pages/creators/creators.interface';
import { CreatorsStore } from '@pages/creators/creators.store';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DatatableBaseComponent } from '@shared/components/datatable-base/datatable-base.component';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  IDatatableConfiguration,
  IRowClickEvent,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { IBadgeData } from '@shared/components/social-badge-list/social-badge-list.interface';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { camelCase } from 'lodash';
import { combineLatest, filter } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

@Component({
  imports: [
    HasPermissionDirective,
    MatButtonModule,
    MatInputModule,
    MatMenuModule,
    TranslateModule,
    MatDialogModule,
    RouterModule,
    IconBadgeComponent,
    DatatableComponent,
    SocialBadgeComponent,
    IconComponent,
  ],
  selector: 'app-creators-datatable',
  styleUrl: './creators-datatable.component.scss',
  templateUrl: './creators-datatable.component.html',
})
export class CreatorsDatatableComponent extends DatatableBaseComponent implements OnInit {
  public readonly creators = input.required<ICreator[] | null>();
  public readonly creatorsLoading = input.required<boolean>();

  public readonly displayedColumns: string[] = ['name', 'campaigns', 'platforms', 'actions'];

  public readonly loadedTableData = signal<boolean>(false);
  public readonly isRedirecting = signal<boolean>(false);
  public readonly dataSource = signal<MatTableDataSource<ICreator>>(new MatTableDataSource<ICreator>([]));

  public readonly EPlatform = EPlatform;

  public readonly skeletonLoaderConfig: ISkeletonLoaderConfig = {
    columns: [
      {
        circleSize: '40px',
        className: 'flex-1 w-[65%]',
        columnPadding: '0 8px',
        hasText: true,
        rowPadding: '8px 0',
        textWidth: '80px',
        type: 'icon',
      },
      {
        circleSize: '24px',
        className: 'w-[14%]',
        hasText: true,
        textWidth: '50px',
        type: 'icon',
      },
      {
        circleSize: '24px',
        className: 'w-[14%]',
        type: 'icon',
      },
      {
        borderRadius: '100vw',
        circleSize: '24px',
        className: 'w-[7%] flex justify-start',
        type: 'icon',
        width: '0',
      },
    ],
    rows: 5,
    showHeaderControls: true,
    showSearchField: true,
  };

  public readonly datatableColumns: TDatatableColumn<ICreator, string>[] = [
    {
      id: 'name',
      isClientSideSearchable: true,
      isSortable: true,
    },
    {
      dataViewStrategy: (row: ICreator) => String(row?.campaignCreators?.length ?? 0),
      id: 'campaigns',
      isSortable: true,
    },
    {
      dataViewStrategy: () => '',
      id: 'platforms',
    },
    {
      dataViewStrategy: () => '',
      id: 'actions',
    },
  ];

  public datatableConfig: IDatatableConfiguration<ICreator> = {};

  private readonly menu = viewChild<MatMenu>('menu');
  private readonly tableStateService: TableStateService = inject(TableStateService);
  private readonly tableStorageKey: string = this.tableStateService.buildKey('creators', 'datatable');

  constructor(
    public readonly store: CreatorsStore,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
  ) {
    super();

    this.datatableConfig = {
      storageKey: this.tableStorageKey,
    };

    effect(() => {
      this.dataSource().data = this.mapCreatorsData(this.creators() ?? []);

      if (!this.store.loading$()) {
        this.loadedTableData.set(true);
      }

      if (this.store.newCreator$()) {
        this.router.navigate(['/creators', this.store.newCreator$()?.id]);
      }
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      }).subscribe(({ datatable }) => {
        this.store.fetchCreators({
          joins: ['campaign_creators', 'channels'],
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search
            ? { search: { searchedFields: ['name__ilike'], searchText: datatable.search ?? '' } }
            : {}),
        });
      }),
    );
  }

  public onDeleteClick(event: Event, creator: ICreator): void {
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant('shared.delete'),
        message: this.translate.instant('pages.creators.datatable.deleteCreatorMessage', {
          name: creator.name,
        }),
        title: this.translate.instant('pages.creators.datatable.deleteCreator'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.store.deleteCreator(creator.id);
        this.menu()?.closed.emit();
      });
  }

  public onRowClick(row: IRowClickEvent<ICreator, string>): void {
    if (!this.permissionService.hasPermission(this.EPermissions.creatorReadOwn)) {
      return;
    }

    const { event, row: creator } = row;

    const isNewTab = event?.metaKey || event?.ctrlKey;

    if (isNewTab) {
      WindowRedirectService.openUrlInNewTab(`/creators/${creator.id}`);

      return;
    }

    this.isRedirecting.set(true);
    this.router.navigate(['/creators', creator.id]);
  }

  private mapCreatorsData(creators: ICreator[]): ICreator[] {
    return creators.map((creator: ICreator) => {
      const platformList = new Map<EPlatform, IBadgeData>();

      creator?.channels?.forEach((channel: IChannel) => {
        platformList.set(camelCase(channel.platform) as EPlatform, {
          id: uuidv4(),
          platform: camelCase(channel.platform) as EPlatform,
          platformCreatorId: channel?.platformCreatorId,
        });
      });

      return {
        ...creator,
        platformList: Array.from(platformList.values()),
      };
    });
  }
}
